/**
 * Sidebar.tsx
 *
 * File-level:
 * - Small reusable sidebar component for admin pages.
 * - Uses CSS theme variables for consistent colors (sidebar background & text).
 *
 * Notes:
 * - Single responsibility: only navigation UI.
 */

import React, { useState } from 'react';
import { Home, Users, Settings as Cog, LogOut, BarChart } from 'lucide-react';
import { useNavigate } from 'react-router';
import ConfirmLogoutModal from './ConfirmLogoutModal';

export interface SidebarProps {
  /** Active route key to highlight current item */
  active?: string;
  /** Called when the user clicks a nav item with the item's key */
  onNavigate?: (key: string) => void;
}

/**
 * Sidebar
 *
 * @description Displays admin navigation. Single-responsibility: only navigation UI.
 */
export default function Sidebar({ active = 'dashboard', onNavigate }: SidebarProps) {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  /**
   * handleClick
   *
   * @param key string - navigation key
   * @description Small helper to call parent navigation handler when provided
   */
  function handleClick(key: string) {
    if (onNavigate) onNavigate(key);
  }

  /**
   * openLogoutConfirm
   *
   * @description Show the confirmation modal instead of immediately logging out.
   */
  function openLogoutConfirm() {
    setShowLogoutConfirm(true);
  }

  /**
   * performLogout
   *
   * @description Clears admin session and redirects to home page. Called after confirmation.
   */
  function performLogout() {
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    } catch {
      // ignore localStorage errors
    }
    setShowLogoutConfirm(false);
    // Navigate to the home page (HashRouter aware)
    navigate('/');
  }

  /**
   * closeLogoutConfirm
   *
   * @description Close the confirmation modal without logging out.
   */
  function closeLogoutConfirm() {
    setShowLogoutConfirm(false);
  }

  return (
    <aside
      className="w-72 border-r p-4 hidden lg:block flex flex-col"
      style={{
        backgroundImage: 'var(--sidebar-bg)',
        color: 'var(--sidebar-text)',
      }}
      aria-label="Admin sidebar"
    >
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-white/10 shadow-md flex items-center justify-center text-white font-bold">
            AD
          </div>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--sidebar-text)' }}>
              Admin Console
            </h2>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Super Admin
            </p>
          </div>
        </div>

        <nav className="space-y-1" aria-label="Admin Navigation">
          <button
            onClick={() => handleClick('dashboard')}
            className={`w-full text-left flex items-center gap-3 p-3 rounded-lg focus:outline-none transition ${
              active === 'dashboard' ? 'bg-white/10 shadow' : 'hover:bg-white/5'
            }`}
            style={{ color: active === 'dashboard' ? 'var(--sidebar-text)' : 'rgba(255,255,255,0.95)' }}
          >
            <Home className="w-4 h-4" />
            <span className="font-medium">Dashboard</span>
          </button>

          <button
            onClick={() => handleClick('users')}
            className={`w-full text-left flex items-center gap-3 p-3 rounded-lg focus:outline-none transition ${
              active === 'users' ? 'bg-white/10 shadow' : 'hover:bg-white/5'
            }`}
            style={{ color: active === 'users' ? 'var(--sidebar-text)' : 'rgba(255,255,255,0.95)' }}
          >
            <Users className="w-4 h-4" />
            <span className="font-medium">User Management</span>
          </button>

          {/* Reports section */}
          <div className="mt-2">
            
            <div className="space-y-1 px-1">
              <button
                onClick={() => handleClick('visit-log')}
                className={`w-full text-left flex items-center gap-3 p-2 rounded-lg focus:outline-none transition ${
                  (active as string) === 'visit-log' ? 'bg-white/10 shadow' : 'hover:bg-white/5'
                }`}
                style={{ color: (active as string) === 'visit-log' ? 'var(--sidebar-text)' : 'rgba(255,255,255,0.95)' }}
              >
                <BarChart className="w-4 h-4" />
                <span className="text-sm">Visit Log</span>
              </button>
              <button
                onClick={() => handleClick('login-log')}
                className={`w-full text-left flex items-center gap-3 p-2 rounded-lg focus:outline-none transition ${
                  (active as string) === 'login-log' ? 'bg-white/10 shadow' : 'hover:bg-white/5'
                }`}
                style={{ color: (active as string) === 'login-log' ? 'var(--sidebar-text)' : 'rgba(255,255,255,0.95)' }}
              >
                <BarChart className="w-4 h-4" />
                <span className="text-sm">Login Log</span>
              </button>
            </div>
          </div>

          <button
            onClick={() => handleClick('settings')}
            className={`w-full text-left flex items-center gap-3 p-3 rounded-lg focus:outline-none transition ${
              active === 'settings' ? 'bg-white/10 shadow' : 'hover:bg-white/5'
            }`}
            style={{ color: active === 'settings' ? 'var(--sidebar-text)' : 'rgba(255,255,255,0.95)' }}
          >
            <Cog className="w-4 h-4" />
            <span className="font-medium">Settings</span>
          </button>
        </nav>

        <div className="mt-8 text-xs" style={{ color: 'rgba(255,255,255,0.75)' }}>
          <p>Shortcuts</p>
          <ul className="mt-2 space-y-2">
            <li className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-emerald-400 rounded-full" />
              Verified
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-amber-400 rounded-full" />
              Pending
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-rose-500 rounded-full" />
              Flagged
            </li>
          </ul>
        </div>
      </div>

      {/* Footer area with Logout */}
      <div className="mt-auto pt-4 border-t">
        <button
          onClick={openLogoutConfirm}
          aria-label="Logout and return to home"
          className="w-full text-left flex items-center gap-3 p-3 rounded-lg focus:outline-none transition hover:bg-white/5 text-rose-400"
        >
          <LogOut className="w-4 h-4" />
          <span className="font-medium">Logout</span>
        </button>
      </div>

      {/* Confirmation modal */}
      <ConfirmLogoutModal
        open={showLogoutConfirm}
        onConfirm={performLogout}
        onClose={closeLogoutConfirm}
      />
    </aside>
  );
}